import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Loader2, LockKeyhole, Mail, UserRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatAuthError } from "@/lib/authErrors";
import { useSeo } from "@/lib/seo";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

const TYPE_LABELS = { privato: "Privato", agenzia: "Nuova agenzia", ufficio: "Ufficio aggiuntivo" };
const PLAN_LABELS = { base: "Privato", premium: "Agenzia Pro", agenzia: "Agenzia Full" };

export default function Register() {
    useSeo({ title: "Crea account | VendiDirect", description: "Crea il tuo account VendiDirect.", noindex: true });
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { setUser, refresh } = useAuth();
    const accountType = ["privato", "agenzia", "ufficio"].includes(params.get("account_type")) ? params.get("account_type") : "privato";
    const planId = ["base", "premium", "agenzia"].includes(params.get("plan")) ? params.get("plan") : "";
    const mode = params.get("mode") === "promo" ? "promo" : "checkout";
    const officeCode = (params.get("office_code") || "").replace(/[^A-Za-z0-9]/g, "").slice(0, 8).toUpperCase();
    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const continueSelectedFlow = async () => {
        if (accountType === "ufficio" && officeCode) {
            await api.post("/onboarding/redeem-code", { code: officeCode });
            await refresh();
            navigate("/analytics-pro", { replace: true });
            return;
        }
        if (planId && mode === "promo") {
            await api.post("/subscription/activate-promo", { plan_id: planId });
            await refresh();
            navigate("/dashboard", { replace: true });
            return;
        }
        if (planId) {
            const { data } = await api.post("/subscription/checkout", { plan_id: planId, origin_url: PUBLIC_SITE_URL });
            window.location.href = data.url;
            return;
        }
        navigate("/dashboard", { replace: true });
    };

    const submit = async (event) => {
        event.preventDefault(); setError("");
        if (form.password !== form.confirm) return setError("Le password non coincidono");
        setBusy(true);
        try {
            const { data } = await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, account_type: accountType, plan_id: planId || null });
            setUser(data.user);
            await continueSelectedFlow();
        } catch (requestError) {
            setError(formatAuthError(requestError?.response?.data?.detail, "Registrazione non riuscita"));
        } finally { setBusy(false); }
    };

    return (
        <main data-testid="register-page" className="login-luxury-page">
            <div className="login-luxury-panel">
                <div className="login-luxury-icon"><Building2 size={25} /></div>
                <h1 data-testid="register-title" className="login-luxury-title text-center">Crea il tuo account</h1>
                <p className="login-luxury-subtitle text-center">Completa la registrazione per continuare con la scelta effettuata.</p>
                <div data-testid="register-selection-summary" className="login-intent-note">
                    <span>Tipo account</span><strong>{TYPE_LABELS[accountType]}</strong>
                    {planId && <><span>Piano</span><strong>{PLAN_LABELS[planId]}</strong></>}
                </div>
                <form data-testid="register-form" onSubmit={submit} className="login-credentials-form">
                    <label className="login-field"><span><UserRound size={14} /> Nome</span><input data-testid="register-name-input" required minLength="2" maxLength="120" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="login-input" placeholder="Mario Rossi" /></label>
                    <label className="login-field"><span><Mail size={14} /> Email</span><input data-testid="register-email-input" required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="login-input" placeholder="nome@email.it" /></label>
                    <label className="login-field"><span><LockKeyhole size={14} /> Password</span><input data-testid="register-password-input" required type="password" minLength="8" maxLength="128" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="login-input" placeholder="Almeno 8 caratteri" /></label>
                    <label className="login-field"><span><LockKeyhole size={14} /> Conferma password</span><input data-testid="register-confirm-input" required type="password" minLength="8" maxLength="128" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="login-input" placeholder="Ripeti la password" /></label>
                    {error && <div data-testid="register-error" className="login-alert login-alert-error">{error}</div>}
                    <button data-testid="register-submit-button" type="submit" disabled={busy} className="login-submit-button">{busy ? <Loader2 className="animate-spin" size={17} /> : <UserRound size={17} />} Crea account e continua</button>
                </form>
                <p className="login-luxury-legal">Creando l’account accetti i nostri <Link data-testid="register-terms-link" to="/termini">Termini</Link> e l’<Link data-testid="register-privacy-link" to="/privacy">Informativa privacy</Link>.</p>
            </div>
        </main>
    );
}